import { ACTIVITY_TYPE_LABELS, type ActivityItem, type ActivityType } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { CalendarClock, Mail, Phone, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { activitiesQuery } from '@/lib/crm';
import { formatMoment } from '@/lib/format';
import { ActivityFormDialog } from './crm-forms';
import { Avatar, Button } from './ui';

const ICON: Record<ActivityType, typeof Phone> = {
  call: Phone,
  meeting: Users,
  todo: CalendarClock,
  email: Mail,
};

/** Строка дела: галочка «выполнено», что и когда, привязка. */
export function ActivityRow({ a, showLinks }: { a: ActivityItem; showLinks?: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [askResult, setAskResult] = useState(false);
  const [result, setResult] = useState('');
  const Icon = ICON[a.type];
  const complete = useMutation({
    mutationFn: (done: boolean) =>
      api(`/crm/activities/${a.id}/complete`, { method: 'POST', body: { done, result } }),
    onSuccess: () => {
      setAskResult(false);
      return qc.invalidateQueries({ queryKey: ['crm'] });
    },
  });
  return (
    <li className="px-4 py-2.5">
      {editing && <ActivityFormDialog activity={a} onClose={() => setEditing(false)} />}
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={Boolean(a.doneAt)}
          disabled={!a.canEdit || complete.isPending}
          onChange={(e) => (e.target.checked ? setAskResult(true) : complete.mutate(false))}
          aria-label={a.doneAt ? 'Вернуть в работу' : 'Отметить выполненным'}
          className="mt-1 size-4 shrink-0 accent-[var(--color-graphite)]"
        />
        <div className="min-w-0 flex-1">
          <button
            type="button"
            disabled={!a.canEdit}
            onClick={() => setEditing(true)}
            className={clsx(
              'text-left',
              a.doneAt && 'text-steel line-through',
              a.canEdit && 'hover:text-link',
            )}
          >
            <Icon className="mr-1.5 inline size-3.5 -translate-y-px text-steel" aria-hidden />
            <span className="sr-only">{ACTIVITY_TYPE_LABELS[a.type]}: </span>
            {a.subject}
          </button>
          <p className="mt-0.5 flex flex-wrap gap-x-2 text-[12.5px] text-steel">
            <span className={clsx(a.isOverdue && 'font-medium text-danger')}>{formatMoment(a.dueAt)}</span>
            {showLinks && a.deal && (
              <Link to="/crm/deals/$id" params={{ id: a.deal.id }} className="link">
                #{a.deal.number} {a.deal.title}
              </Link>
            )}
            {showLinks && !a.deal && a.contact && (
              <Link to="/crm/contacts/$id" params={{ id: a.contact.id }} className="link">
                {a.contact.fullName}
              </Link>
            )}
          </p>
          {a.result && <p className="mt-1 text-[13px] text-ink-soft">Итог: {a.result}</p>}
          {askResult && (
            <form
              className="mt-2 flex flex-wrap gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                complete.mutate(true);
              }}
            >
              <input
                className="input max-w-[360px] flex-1"
                value={result}
                onChange={(e) => setResult(e.target.value)}
                placeholder="Итог (необязательно)"
                aria-label="Итог"
              />
              <Button type="submit" size="sm" loading={complete.isPending}>
                Выполнено
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAskResult(false)}>
                Отмена
              </Button>
            </form>
          )}
        </div>
        <span title={`Ответственный: ${a.responsible.fullName}`}>
          <Avatar name={a.responsible.fullName} userId={a.responsible.userId} size={22} />
        </span>
      </div>
    </li>
  );
}

/** Дела в карточке сделки или клиента. */
export function ActivitiesPanel({ dealId, contactId }: { dealId?: string; contactId?: string }) {
  const q = useQuery(activitiesQuery({ dealId, contactId }));
  const [adding, setAdding] = useState(false);
  const open = q.data?.filter((a) => !a.doneAt) ?? [];
  const done = q.data?.filter((a) => a.doneAt) ?? [];
  const [showDone, setShowDone] = useState(false);
  return (
    <section className="panel">
      {adding && (
        <ActivityFormDialog dealId={dealId} contactId={contactId} onClose={() => setAdding(false)} />
      )}
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="font-semibold">
          Дела
          {open.length > 0 && <span className="ml-2 font-normal tabular-nums text-steel">{open.length}</span>}
        </h2>
        <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden />
          Запланировать
        </Button>
      </div>
      {open.length === 0 && (
        <p className="px-4 py-3 text-[13.5px] text-steel">
          Нет запланированных дел. Звонок или встреча с датой — система напомнит вовремя.
        </p>
      )}
      <ul className="divide-y divide-line">
        {open.map((a) => (
          <ActivityRow key={a.id} a={a} />
        ))}
        {showDone && done.map((a) => <ActivityRow key={a.id} a={a} />)}
      </ul>
      {done.length > 0 && (
        <button type="button" className="link px-4 py-2 text-[13px]" onClick={() => setShowDone(!showDone)}>
          {showDone ? 'Скрыть выполненные' : `Выполненные: ${done.length}`}
        </button>
      )}
    </section>
  );
}
